import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { useDeleteUsageApi, usePatchUsageApi, usePostInstrumentUsageApi } from 'api/mypage/usage';
import { Flex } from 'components/shared/flex';
import { DateField, DateTimeField, StringField } from 'components/shared/form/input';
import Modal, { ModalState } from 'components/shared/modal';
import ThemedButton from 'components/shared/themed_button';
import { GlobalStateContext } from 'contexts/global_state_context';
import { ID } from 'entities';
import { Usage, UsageForm } from 'entities/usage';
import { ApiError, Form, useForm } from 'rac';
import React, { useContext, useEffect } from 'react';
import { useEffectSkipFirst } from 'utils/hooks';

const UsageFormContent: React.FC<{ form: Form<UsageForm>; apiError: ApiError }> = ({ form, apiError }) => {
  // 利用は実績の記録なので、未来の日時は選べないようにする（バックエンドでも検証する）
  const now = new Date();

  return (
    <>
      <Flex>
        {form.object.allDay ? (
          <>
            <DateField
              label="利用開始日"
              attr="startedAt"
              form={form}
              apiError={apiError}
              PickerProps={{ maxDate: now }}
              required
            />
            <DateField
              label="利用終了日"
              attr="endedAt"
              form={form}
              apiError={apiError}
              PickerProps={{ maxDate: now }}
              required
            />
          </>
        ) : (
          <>
            <DateTimeField
              label="利用開始日時"
              attr="startedAt"
              form={form}
              apiError={apiError}
              PickerProps={{ maxDateTime: now }}
              required
            />
            <DateTimeField
              label="利用終了日時"
              attr="endedAt"
              form={form}
              apiError={apiError}
              PickerProps={{ maxDateTime: now }}
              required
            />
          </>
        )}
      </Flex>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!form.object.allDay}
            onChange={(e) => {
              form.update((f) => (f.allDay = e.target.checked));
            }}
          />
        }
        label="終日"
      />
      <Flex>
        <StringField label="利用内容" attr="content" form={form} apiError={apiError} multiline minRows={3} />
      </Flex>
    </>
  );
};

type NewUsageFormDialogProps = {
  modalState: ModalState;
  instrumentId: ID;
  onComplete: (usage: Usage) => void;
};

export const NewUsageFormDialog: React.FC<NewUsageFormDialogProps> = (props) => {
  const globalState = useContext(GlobalStateContext);
  const form = useForm<UsageForm>({}, 'usage');
  const api = usePostInstrumentUsageApi();

  useEffect(() => {
    if (!props.modalState.open) return;
    form.resetForm();
  }, [props.modalState.open]);

  useEffectSkipFirst(() => {
    globalState.setLoading(api.loading);
    if (api.isSuccess()) {
      props.onComplete(api.response.usage);
    }
  }, [api.loading]);

  return (
    <Modal
      modalState={props.modalState}
      modalId="new-usage"
      title="利用の登録"
      size="sm"
      footer={
        <>
          <ThemedButton onClick={() => props.modalState.setOpen(false)} color="secondary">
            閉じる
          </ThemedButton>
          <ThemedButton onClick={() => api.execute(props.instrumentId, form)} color="success">
            登録
          </ThemedButton>
        </>
      }
    >
      <UsageFormContent form={form} apiError={api.apiError} />
    </Modal>
  );
};

type EditUsageFormDialogProps = {
  modalState: ModalState;
  usage: Usage;
  onComplete: (usage?: Usage) => void;
};

export const EditUsageFormDialog: React.FC<EditUsageFormDialogProps> = (props) => {
  const globalState = useContext(GlobalStateContext);
  const form = useForm<UsageForm>(props.usage, 'usage');
  const patchApi = usePatchUsageApi();
  const deleteApi = useDeleteUsageApi();

  useEffect(() => {
    if (!props.modalState.open) return;
    form.set(props.usage);
  }, [props.modalState.open, props.usage]);

  useEffectSkipFirst(() => {
    globalState.setLoading(patchApi.loading);
    if (patchApi.isSuccess()) {
      props.onComplete(patchApi.response.usage);
    }
  }, [patchApi.loading]);

  useEffectSkipFirst(() => {
    globalState.setLoading(deleteApi.loading);
    if (deleteApi.isSuccess()) {
      props.onComplete();
    }
  }, [deleteApi.loading]);

  const handleClickDelete = () => {
    globalState.confirm('本当に削除しますか？', (event) => {
      if (event === 'ok') {
        deleteApi.execute(props.usage.id!);
      }
    });
  };

  return (
    <Modal
      modalState={props.modalState}
      modalId="edit-usage"
      title="利用の編集"
      size="sm"
      footer={
        <Flex justifyContent="space-between" style={{ width: '100%' }}>
          <Tooltip title={props.usage.canDelete ? '' : '管理者・登録者のみ削除可能です。'}>
            <div>
              <ThemedButton color="error" onClick={handleClickDelete} disabled={!props.usage.canDelete}>
                削除
              </ThemedButton>
            </div>
          </Tooltip>
          <Flex>
            <ThemedButton onClick={() => props.modalState.setOpen(false)} color="secondary" style={{ marginRight: 10 }}>
              閉じる
            </ThemedButton>
            <ThemedButton onClick={() => patchApi.execute(form.object)} color="success">
              更新
            </ThemedButton>
          </Flex>
        </Flex>
      }
    >
      <UsageFormContent form={form} apiError={patchApi.apiError} />
    </Modal>
  );
};
